<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
/* global axios */
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import ShipmentTrackingModal from './ShipmentTrackingModal.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import {
  isWhatsAppBsuid,
  whatsAppUsernameHandle,
} from 'dashboard/helper/whatsappIdentity';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import {
  useErpCustomerIdentity,
  ERP_CUSTOMER_CODE,
} from 'dashboard/composables/useErpCustomerIdentity';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const {
  isAWebWidgetInbox,
  isAWhatsAppChannel,
  isAFacebookInbox,
  isAnInstagramChannel,
} = useInbox();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value?.hmac_verified ?? false;
});

const currentContact = computed(() => {
  const contact = store.getters['contacts/getContact'](
    props.chat.meta?.sender?.id
  );
  return contact || {};
});

const formattedPhoneNumber = computed(() => {
  const name = currentContact.value.name || '';
  // Show the WhatsApp number this conversation actually came in on
  // (contact_inbox.source_id) rather than the contact's saved phone. The same
  // contact can write from several numbers — each its own conversation — and
  // the header should reflect which number this thread belongs to. Fall back to
  // the contact's saved phone when the source id isn't available.
  const sourceId = chatMetadata.value?.source_id;
  // A WhatsApp username customer's source_id is a business-scoped user ID, not a
  // number. Digit-stripping it yields a convincing 16-digit fake that an agent
  // would copy into a COD sales order, so never let one reach the phone chip.
  const phone =
    (isAWhatsAppChannel.value && !isWhatsAppBsuid(sourceId) && sourceId) ||
    currentContact.value.phone_number;

  if (
    isAWhatsAppChannel.value &&
    phone &&
    name !== phone &&
    !name.includes(phone)
  ) {
    let cleanPhone = String(phone).replace(/\D/g, '');
    if (cleanPhone.startsWith('2')) {
      cleanPhone = cleanPhone.substring(1);
    }
    return cleanPhone;
  }

  return null;
});

// A WhatsApp username customer has no phone number to show. Their @username is the
// only handle an agent has to recognise them by, so it takes the phone chip's place.
const whatsAppHandle = computed(() => {
  if (!isAWhatsAppChannel.value) return '';
  // Only for a customer who genuinely has no phone number — Meta sends profile.username
  // for ordinary customers too, and this chip's tooltip asserts the number is hidden.
  if (!isWhatsAppBsuid(chatMetadata.value?.source_id)) return '';

  return whatsAppUsernameHandle(currentContact.value.additional_attributes);
});

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

// Eltafouk: a comment thread has no ERP/customer/shipment context — the author
// is a Facebook commenter, not an order/customer. Hide that chrome here. The
// inbox `is_comment_inbox` flag is the durable signal; `comment_id` (written by
// n8n) corroborates it and can be momentarily absent right after creation.
const isCommentSection = computed(
  () =>
    Boolean(inbox.value?.is_comment_inbox) ||
    props.chat?.custom_attributes?.comment_id != null
);

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

// Eltafouk: ERP context shown in the header. The bridge mirrors these onto the
// contact's additional_attributes; the conversation ContactPanel loads the full
// contact (contacts/show) so they're available here.
const ERP_BASE_URL = 'https://erp.eltafouk.com';

// The chip, the code and the blue check now live in useErpCustomerIdentity so
// the conversation LIST renders the exact same three things off the exact same
// rules — including the CUST-###### guard that keeps a Facebook PSID in
// `identifier` from passing as an ERP customer.
const { classificationChip, classificationAvatar, erpCustomer, isErpLinked } =
  useErpCustomerIdentity(currentContact);

// Teacher's subject(s). Lives on the ERP Customer (custom_teacher_subjects);
// shown only for teachers. The value reaches the contact via the bridge mirror
// (synced into additional_attributes) — until that sync lands the slot stays empty.
//
// 🚨 القيمة بتطلع طويلة ومكررة الشكل: «فيزياء, Physics, علوم متكاملة,
// Integrated Sciences». **ماتشيلش الإنجليزي منها.** كان فيه تعديل بيعمل كده
// (PR#224) على أساس إنه ترجمة مكررة، وده كان **غلط** — الاسم الإنجليزي بيعني
// إن المدرس بيدرّس المادة في مسار **اللغات**، مش نفس المادة مكتوبة تاني.
//
// الدليل من الـERP (نسبة المدرسين اللي اشتروا كتب «لغات تالتة ثانوي» فعلاً):
//   «Chemistry» بس            → ٦٢٪  (٢٩ مدرس)
//   «كيمياء + Chemistry»       → ١٧٪  (١٦١ مدرس)
//   «كيمياء» بس (مجموعة ضابطة) →  ٤٪  (٨٥٥ مدرس)
// يعني اللي متعلّم عليه Chemistry بيشتري لغات بـ١٥ ضعف المعدل. والشركة عندها
// خط إنتاج كامل للمسارين («عربي تالتة ثانوي» / «لغات تالتة ثانوي»، وحتى
// «البابل شيت العربي» و«البابل شيت لغات»).
//
// فشيل الإنجليزي = إخفاء إن المدرس بيدرّس لغات كمان. الطول تمنه مقبول قدام ده.
const teacherSubject = computed(() => {
  const aa = currentContact.value.additional_attributes;
  if (aa?.custom_customer_classification !== 'Teacher') return '';
  const raw = aa.custom_teacher_subjects;
  // 🚨 نفس الـJSONB بيشيل مصفوفات فعلاً (custom_addresses)، والاستيراد بيكتب
  // الحقول زي ما هي — والحقل ده أصلاً Table في الـERP. فالمصفوفة تتعرض، ماتتخفيش:
  // إخفاؤها هو نفس الغلط اللي الكوميت ده بيصلحه.
  if (typeof raw !== 'string' && !Array.isArray(raw)) return '';
  const parts = Array.isArray(raw) ? raw : raw.split(/[,،]/);
  // تنظيف وتوحيد شكل بس — **من غير ما نشيل ولا عنصر**. القيمة «   » أو «,»
  // كانت بتطلع شريحة بنفسجية فاضية فيها أيقونة كتاب من غير نص.
  return parts
    .map(part => String(part).trim())
    .filter(Boolean)
    .join('، ');
});

// Eltafouk: اسم العميل زي ما هو مسجّل في الـERP.
//
// على ماسنجر وإنستجرام الكارت شايل **اسم البروفايل** («Mohamed Rabei») — وده
// اللي الموظفة بتعرف بيه المحادثة، فالجسر مابيغيّرش الاسم عن قصد
// (`mirror_identity_contact`: NOT `name`). بس النتيجة إنها عمرها ماتشوف اسمه
// عندنا («محمد ربيع محمد عبدالعزيز») إلا لو فتحت الـERP.
//
// 🚨 بنعرضه **بس لما يكون مختلف** عن اسم الكارت: على واتساب الجسر بيسمّي الكارت
// باسم العميل أصلاً، فعرضه تاني بيبقى تكرار بياخد مساحة في سطر ضيّق أصلاً.
const erpCustomerName = computed(() => {
  const name = (
    currentContact.value.additional_attributes?.erp_customer_name || ''
  ).trim();
  if (!name) return '';
  const shown = (currentContact.value.name || '').trim();
  return name === shown ? '' : name;
});

const erpLabel = computed(() => t('CONVERSATION.HEADER.ERP_LABEL'));

// Eltafouk: every customer in ERPNext is named CUST-<digits> (26,773 of 26,773
// at the time of writing). `erpCustomer` falls back to `identifier`, which is
// frequently NOT a customer code — a phone number, say — and a link built from
// one lands on a «not found» form or an unfiltered list of every order. A value
// that cannot be a customer produces no link at all: the whole toolbar below is
// gated on erpCustomerUrl.
//
// ERP_CUSTOMER_CODE is imported, not re-declared. Two branches once added the
// same constant with the same regex on the same day (#189 gating `erpCustomer`,
// #190 gating the links); git merged both cleanly because they sit in different
// hunks, and the duplicate `const` only surfaced as a build failure in
// `assets:precompile` — Identifier 'ERP_CUSTOMER_CODE' has already been declared.
// Keeping the check here as well is deliberate and free: `erpCustomer` is already
// filtered, so this is a second, independent gate on the link builder rather than
// a redundant one.
const erpCustomerCode = computed(() => {
  const value = (erpCustomer.value || '').trim();
  return ERP_CUSTOMER_CODE.test(value) ? value : '';
});

// Open-in-ERP target (customer page) doubles as the "linked" badge.
const erpCustomerUrl = computed(() =>
  erpCustomerCode.value
    ? `${ERP_BASE_URL}/app/customer/${encodeURIComponent(erpCustomerCode.value)}`
    : ''
);

// Quick deep-links to the customer's orders / invoices / statement in ERP.
// Eltafouk: عدّادين على «أوردرات» و«فواتير» — طلب المالك ٢٣/٨.
//
// 🚨 العدّاد بيعدّ **نفس** اللي اللينك بيعرضه، مش أكتر:
//   · الأوردرات = المفتوحة بس (نفس فلتر صفحة «أوردرات العميل»).
//   · الفواتير  = موسم النهاردة بس، لأن قايمة الفواتير في الـERP بترجع للموسم
//     الحالي في كل فتحة. لو عدّينا كل الفواتير، الموظفة تشوف ٣٠ وتفتح فتلاقي ٤
//     — وساعتها بتبطّل تصدّق العدّاد كله.
//
// 🚨 وكاش زي الشحنات: الموظفة بتقلّب بين المحادثات بسرعة، ومن غير الكاش كل
// رجوع لمحادثة بيوقّف العدّاد على «—» لحد ما الشبكة ترد.
const erpCounts = ref(null);
const erpCountsCache = new Map();

const fetchErpCounts = async () => {
  const contactId = currentContact.value?.id;
  const code = erpCustomerCode.value;
  if (isCommentSection.value || !contactId || !code) {
    erpCounts.value = null;
    return;
  }
  if (erpCountsCache.has(code)) erpCounts.value = erpCountsCache.get(code);
  try {
    const { data } = await axios.get(
      `/api/v1/accounts/${accountId.value}/contacts/${contactId}/erp_counts`
    );
    // الموظفة ممكن تكون بدّلت المحادثة والطلب لسه في الطريق — رد قديم
    // مايتكتبش فوق الجديد.
    if (erpCustomerCode.value !== code) return;
    erpCounts.value = data || null;
    erpCountsCache.set(code, data || null);
  } catch (error) {
    // عدّاد ناقص أهون من رسالة خطأ — بنسيب آخر قيمة معروفة.
  }
};

watch(
  () => erpCustomerCode.value,
  code => {
    if (code) fetchErpCounts();
    else erpCounts.value = null;
  },
  { immediate: true }
);

// 🚨 خريطة صريحة، مش «أوردرات وإلا فواتير».
// بلاغ المالك ٢٣/٨: «ليه كشف الحساب عليه عداد! إيه كشف الحساب ٢ دي!» — الشرط
// الثلاثي كان بيدّي عدّاد الفواتير لأي زرار مش «أوردرات»، فكشف الحساب ورث رقم
// مالوش أي معنى عنده. كشف الحساب مش قابل للعدّ أصلاً: هو رصيد، مش مجموعة صفوف.
const COUNT_FIELD = { orders: 'open_orders', invoices: 'invoices' };

const countFor = key => {
  const field = COUNT_FIELD[key];
  if (!field) return null;
  const n = (erpCounts.value || {})[field];
  return typeof n === 'number' ? n : null;
};

const countTone = key => {
  const n = countFor(key);
  if (key === 'orders') {
    return n > 0 ? 'bg-n-amber-9 text-white' : 'bg-n-alpha-2 text-n-slate-11';
  }
  return n > 0 ? 'bg-n-blue-9 text-white' : 'bg-n-alpha-2 text-n-slate-11';
};

const erpLinks = computed(() => {
  const code = erpCustomerCode.value;
  if (!code) return [];
  const c = encodeURIComponent(code);
  return [
    {
      key: 'orders',
      short: t('CONVERSATION.HEADER.ERP_LINKS.ORDERS.SHORT'),
      label: t('CONVERSATION.HEADER.ERP_LINKS.ORDERS.LABEL'),
      icon: 'i-lucide-shopping-cart',
      // A path, not a query string: an agent whose browser has no ERP session
      // is bounced to /login, and frappe redirects back with the PATH only —
      // ?customer=… would be dropped and they would land on an unfiltered list
      // of every order. This route opens on this customer alone, with no season,
      // no saved pills and no status filter left over. See the «أوردرات العميل»
      // page in custom_addons.
      url: `${ERP_BASE_URL}/app/customer-orders/${c}`,
    },
    {
      key: 'invoices',
      short: t('CONVERSATION.HEADER.ERP_LINKS.INVOICES.SHORT'),
      label: t('CONVERSATION.HEADER.ERP_LINKS.INVOICES.LABEL'),
      icon: 'i-lucide-receipt',
      url: `${ERP_BASE_URL}/app/sales-invoice?customer=${c}`,
    },
    {
      key: 'statement',
      short: t('CONVERSATION.HEADER.ERP_LINKS.STATEMENT.SHORT'),
      label: t('CONVERSATION.HEADER.ERP_LINKS.STATEMENT.LABEL'),
      icon: 'i-lucide-file-text',
      url: `${ERP_BASE_URL}/app/query-report/General%20Ledger?party_type=Customer&party=${c}`,
    },
  ];
});

// ===== Eltafouk: زرار «أوردر جديد» — الطريق الأساسي من الشات للأوردر =====
// المالك: «زرار في رأس المحادثة جنب الزراير الموجودة، بس يكون أوضح — إنشاء order
// للعميل. ولو هو عميل مش مسجل، يودينا الأول على صفحته نعمل له ملف، وبعد ما نكمّل
// ملفه نروح نعمل له order».
//
// 🚨 زرار واحد ومسار واحد ولون واحد. الزرار مابيخمّنش هو العميل مسجّل في ERP
// ولا لأ، ومابيقولش للموظف حاجة عن حالة العميل. اللي في Chatwoot هو نسخة
// مرآة (additional_attributes.erp_customer) بيكتبها الـ bridge وممكن تكون لسه ماوصلتش،
// أو العميل يكون موجود فعلاً في ERP برقم التليفون وماتربطش هنا. لو بنينا على
// النسخة دي زرار بيقول «العميل مش مسجّل» كنا هنفتح ملف تاني لعميل موجود = دوبليكات.
// اللي بيرد على السؤال ده هو ERP لوحده عن طريق
// custom_addons.api.chatwoot_link.resolve_party جوّا الصفحة اللي الزرار بيفتحها.
//
// 🚨 كل قيمة بتتحط في المسار (path) مش في ?query. السبب مش تجميل: الموظف اللي
// جلسته في ERP خلصت بيتحوّل على /login، وfrappe بيرجّعه على الـ path بس
// (frappe/www/app.py) — فالـ query string بيضيع وكان هيوصل لصفحة بلا أي سياق.
// نفس السبب اللي خلّى لينك «أوردرات العميل» فوق يتبني كـ path في #190.
//
// العقد المتفق عليه مع صفحة ERP — ستة مقاطع بالظبط، بالترتيب ده، مفيش سابع:
//   /app/chatwoot-order/<channel>/<inbox_id>/<source_id>/<contact_id>/<conversation_id>/<last_message_id>
const ERP_ORDER_ROUTE = `${ERP_BASE_URL}/app/chatwoot-order`;

// أي قيمة ناقصة بتتكتب شرطة بدل ما تتشال، عشان ترتيب المقاطع ما يزحفش ويبقى
// الـ inbox في خانة الـ source_id.
const ERP_CHAT_CONTEXT_MISSING = '-';

// encodeURIComponent بيخلّي أي رمز غريب في source_id (بريد، @، مسافة، /) ما يكسرش المسار.
// frappe بيقطّع الـ route على "/" قبل ما يفكّ الترميز (frappe/public/js/frappe/router.js
// — get_sub_path بيعمل split('/') الأول وبعدين decodeURIComponent لكل مقطع)، فالـ %2F
// بيوصل مقطع واحد سليم مش مقطعين.
const erpChatSegment = value => {
  const raw = value === 0 || value ? String(value).trim() : '';
  return raw ? encodeURIComponent(raw) : ERP_CHAT_CONTEXT_MISSING;
};

// 🚨 المقطع الأول: القناة. من غيره ERP مايقدرش يعرف الـ source_id ده إيه: رقم
// تليفون؟ ولا PSID بتاع فيسبوك؟ ولا bsuid بتاع واتساب؟ دي بالظبط اللي شحنت كـباج
// واتصلحت في #189. واحد من أربعة: whatsapp | messenger | instagram | other.
//
// 🚨 whatsapp معناها بالتحديد: الـ source_id ده رقم تليفون تقدر تتصل بيه وتحطه في
// موبايل العميل. عميل الـ WhatsApp Username مالوش رقم — source_id بتاعه bsuid زي
// "EG.1070034385706394"، ولو شيلت منه الحروف بيطلع رقم مقنع من 16 خانة مش تليفون
// خالص — وموظف ينقله في أوردر دفع-عند-الاستلام يشحن للهوا. عشان كده المحادثة دي
// بتتبعت بـ other مش whatsapp: يعني "معاك مفتاح للشخص بس مش رقم تليفون — ماتحطهوش في
// خانة الموبايل". القرار بيتاخد هنا مرة واحدة بدل ما ERP يعيد كتابة نفس الـ regex
// ويقع في نفس فخ تكرار ERP_CUSTOMER_CODE اللي كسر البناء في #191.
//
// 🚨🚨 والقناة بتتاخد من **حمولة المحادثة نفسها** (`meta.channel`) الأول، مش من
// مخزن الصناديق. `useInbox()` وإخواته بيقروا من `inboxes/getInbox`، واللي
// بيرجّع `{}` لو الصندوق لسه ماوصلش للمخزن — وساعتها كل الفحوصات بترجع false،
// القناة تبقى "other"، **والحارس تحت بيخفي الزرار خالص**.
// بلاغ المالك ٢٣/٨: محادثة واتساب سليمة (source_id و sender و channel كلهم في
// الحمولة، اتأكدت من الـAPI) والزرار مش ظاهر. الحمولة فيها
// `meta.channel = "Channel::Whatsapp"` — يعني الحقيقة كانت موجودة والكود كان
// بيسأل المصدر الغلط.
const CHANNEL_FROM_META = {
  'Channel::Whatsapp': 'whatsapp',
  'Channel::FacebookPage': 'messenger',
  'Channel::Instagram': 'instagram',
};

const erpChatChannel = computed(() => {
  const fromMeta = CHANNEL_FROM_META[erpChatFacts.value.channel];
  const channel =
    fromMeta ||
    (isAWhatsAppChannel.value && 'whatsapp') ||
    (isAFacebookInbox.value && 'messenger') ||
    (isAnInstagramChannel.value && 'instagram') ||
    '';
  if (!channel) return 'other';
  if (
    channel === 'whatsapp' &&
    isWhatsAppBsuid(chatMetadata.value?.source_id)
  ) {
    return 'other';
  }
  return channel;
});

// آخر رسالة حقيقية وقت الضغط — ده اللي هيخلّي ERP يقدر يرجّعنا بعدين لنفس النقطة.
// props.chat هو نفسه getSelectedChat (ConversationBox بيمرّره) فالرسايل المحمّلة موجودة فيه.
// 🚨 مش بنستخدم getLastMessage() من conversationHelper: لو مفيش ولا رسالة غير-نشاط
// لا في الـ store ولا في last_non_activity_message، بيرجّع آخر عنصر في المصفوفة حتى لو
// كان سطر نشاط زي «تم حل المحادثة» (message_type === 2). بنفلتر إحنا بنفسنا، ولو
// مفيش رسالة حقيقية بنسيب المكان شرطة بدل ما نبعت لـ ERP رقم سطر نشاط.
const ACTIVITY_MESSAGE_TYPE = 2;

const erpLastMessageId = computed(() => {
  const chat = props.chat;
  const messages = Array.isArray(chat?.messages) ? chat.messages : [];
  const real = [...messages, chat?.last_non_activity_message].filter(
    m => m?.id && m.message_type !== ACTIVITY_MESSAGE_TYPE
  );
  if (!real.length) return '';
  return real.reduce((a, b) =>
    (b.created_at || 0) >= (a.created_at || 0) ? b : a
  ).id;
});

// 🚨 كل المقاطع بتتقرا من props.chat مش من الكونتاكت المحمّل (currentContact). السبب:
// contacts/getContact بيرجّع {} للكونتاكت اللي لسه ماتحمّلش (store/modules/contacts/getters.js)،
// والـ conversations/actions.js بيطلب contacts/show من غير await — يعني في أول لحظات فتح أي
// محادثة (وللأبد لو الطلب فشل) الكونتاكت فاضي. meta.sender.id جاي مع المحادثة نفسها
// (_conversation.json.jbuilder) فمفيش رحلة ذهاب وعودة أصلاً ومفيش حالة كاذبة.
// 🚨🚨 المعرّف بيتمسح من الذاكرة مع أول تحديث لحظي — والزرار كان بيختفي وما
// بيرجعش غير بإعادة تحميل الصفحة. بلاغ من الإنتاج، واتأكد بالتجربة:
// `UPDATE_CONVERSATION` (store/modules/conversations/index.js) بيعمل
// `{ ...selectedConversation, ...updates }` — دمج **سطحي**. `source_id` عايش
// جوّه `meta`، وحمولة `conversation.updated` بتبعت `meta` تاني من غيره، فالكائن
// كله بيتستبدل والمعرّف بيروح. أي رسالة جديدة أو تغيير حالة أو إسناد بيعمل كده،
// وكل تحديث بعده بيفضل ناقص — عشان كده الاختفاء دايم لحد الـrefresh.
// (الأربع روابط القديمة نجت لأنها بتقرا من مخزن **الكونتاكت**، مش من المحادثة.)
//
// العلاج: نفتكر آخر معرّف شفناه لكل محادثة. ده مش تحوّط: `contact_inbox.source_id`
// **مايتغيّرش أبدًا** لمحادثة واحدة (قيد الفريدية عليه في الداتابيز)، فالقيمة
// المحفوظة صح للأبد. والذاكرة دي بتموت مع الصفحة.
// 🚨🚨 والقناة معاه في نفس الذاكرة، ومش سهو:
// حمولة التحديث اللحظي (`Conversations::EventDataPresenter#push_meta`) بتبعت
// `meta` فيها **خمس حاجات بس**: sender · assignee · assignee_type · team ·
// hmac_verified. **مافيهاش `source_id` ولا `channel`** — والاتنين بيضيعوا في
// نفس اللحظة.
//
// أنا صلّحت `source_id` بالذاكرة دي، وبعدها بيومين خلّيت الحارس يقرا `channel`
// من نفس الـ`meta` **ونسيت أحفظه زيّه** — فالزرار رجع يختفي من باب تاني، وبلاغ
// المالك ٢٤/٨ كان بالحرف: «مش بيظهر غير بعد ما أعمل refresh».
//
// الاتنين ليهم نفس الخاصية اللي بتخلّي الحفظ آمن: **مايتغيّروش أبدًا** لمحادثة
// واحدة. فأي حقيقة تانية نحتاجها من `meta` بعد كده، مكانها هنا.
const erpChatMemo = new Map();

/** الحقيقتين اللي التحديث اللحظي بيمسحهم — من الحمولة الحية وإلا من الذاكرة. */
const erpChatFacts = computed(() => {
  const conversationId = props.chat?.id;
  const liveSourceId = chatMetadata.value?.source_id;
  const liveChannel = chatMetadata.value?.channel;

  if (conversationId && (liveSourceId || liveChannel)) {
    const prev = erpChatMemo.get(conversationId) || {};
    erpChatMemo.set(conversationId, {
      // كل واحد بيتحدّث لوحده: حمولة فيها واحد بس مالهاش تمسح التاني.
      sourceId: liveSourceId || prev.sourceId,
      channel: liveChannel || prev.channel,
    });
  }

  const memo = (conversationId && erpChatMemo.get(conversationId)) || {};
  return {
    sourceId: liveSourceId || memo.sourceId || '',
    channel: liveChannel || memo.channel || '',
  };
});

const erpChatContextPath = computed(() => {
  const inboxId = props.chat?.inbox_id;
  const conversationId = props.chat?.id;
  const contactId = chatMetadata.value?.sender?.id;

  const sourceId = erpChatFacts.value.sourceId;

  // من غير inbox، أو من غير مفتاح الشخص (source_id)، أو من غير رقم المحادثة، الـ ERP
  // مش هيعرف يوصل لمين ولا يختم الأوردر بإيه — الزرار ما يظهرش أصلاً.
  //
  // 🚨 `source_id` **إجباري**، مش «هو أو رقم الكونتاكت»: صفحة الـ ERP اللي الزرار
  // بيوديها بترفض الطلب من غيره (`chatwoot_order.py`: `if not inbox_id or not
  // source_id`). لو سمحنا للزرار يظهر برقم الكونتاكت لوحده، الموظف كان هيدوس ويوصل
  // لصفحة بتقوله «الرابط مش مفهوم» — الطرفين لازم يقولوا نفس الشرط.
  if (!inboxId || !conversationId || !sourceId) return '';

  // 🚨 والزرار كمان ما يظهرش قبل ما بيانات الـ inbox نفسها توصل. مقطع القناة بيتحسب
  // من نوع الـ inbox، ولو الـ inboxes لسه بتتحمّل كل الفحوصات بترجع false والقناة
  // كانت هتتبعت «other» لمحادثة واتساب — يعني ERP يقول للموظف «اكتب الموبايل» وهو
  // أصلاً معاه الرقم. نستنى الحقيقة بدل ما نبعت تخمين.
  //
  // 🚨 والفحص على `channel_type` مش على `inbox.value` نفسه: الـ getter بيرجّع **كائن
  // فاضي** `{}` للـ inbox اللي لسه ماوصلش (`inboxes.js::getInbox` بيعمل `inbox || {}`)،
  // و`{}` قيمة صحيحة — يعني `if (!inbox.value)` **عمره ما بيولّع**. `channel_type`
  // بيبان بس لما السجل الحقيقي يوصل، فهو الفحص الوحيد اللي بيفرق.
  //
  // 🚨🚨 و`channel_type` بالشرطة السفلية، **مش** `channelType`. الملف ده فيه
  // مصدرين مختلفين للـ inbox: `inbox` المحلي (سطر ١٣٩) بياخد من
  // `inboxes/getInbox` وبيرجّع السجل **زي ما هو** (snake_case)، بينما
  // `useInbox()` بياخد من `getInboxById` وبيعمل camelCase. اتقاس في المتصفح:
  // `inbox.value.channelType` = undefined دايمًا على الكائن ده، فالحارس بصيغة
  // الجمل كان بيخفي الزرار **على كل المحادثات** — وهو نفسه العيب اللي المفروض
  // يصلّحه. و`isCommentSection` فوق بيستخدم `is_comment_inbox` بنفس الصيغة، وده
  // يأكّد إن ده عرف الكائن ده.
  // 🚨 الحارس ده كان `if (!inbox.value?.channel_type) return ''` — بيعتمد على
  // مخزن الصناديق، وده اللي خفى الزرار على محادثة سليمة تمامًا. بقى بيعتمد على
  // نفس الحمولة اللي جابت الـ source_id: لو المحادثة معاها قناة معروفة يبقى
  // إحنا واثقين، ولو مافيش قناة خالص نستنى بدل ما نبعت تخمين.
  if (!erpChatFacts.value.channel && !inbox.value?.channel_type) return '';

  const segments = [
    erpChatChannel.value,
    erpChatSegment(inboxId),
    erpChatSegment(sourceId),
    erpChatSegment(contactId),
    erpChatSegment(conversationId),
    erpChatSegment(erpLastMessageId.value),
  ];
  return segments.join('/');
});

const createOrderUrl = computed(() => {
  if (isCommentSection.value) return '';
  const context = erpChatContextPath.value;
  return context ? `${ERP_ORDER_ROUTE}/${context}` : '';
});

const shipment = ref(null);
const isLoadingShipment = ref(false);
const isRefreshingShipment = ref(false);
// Contact id the currently loaded shipment belongs to, captured at load time so
// a refresh always targets the right contact even if the user switched chats.
const shipmentContactId = ref(null);
// Eltafouk: per-contact shipment cache. Fetching a shipment is a LIVE round-trip
// to ERPNext (up to ~15s); without this the pill blanked + re-queried on every
// open / re-evaluation, so it visibly vanished for several seconds each time.
// The cache lets us paint the last known pill instantly while a refresh runs in
// the background. Survives conversation switches (the header instance persists).
const shipmentCache = new Map();

const fetchShipments = async () => {
  const contactId = currentContact.value?.id;
  if (isCommentSection.value || !contactId || !isErpLinked.value) {
    shipment.value = null;
    shipmentContactId.value = null;
    return;
  }
  shipmentContactId.value = contactId;
  // Show the cached pill immediately (no blank) while we refresh in the background.
  if (shipmentCache.has(contactId)) {
    shipment.value = shipmentCache.get(contactId);
  }

  isLoadingShipment.value = true;
  try {
    const response = await axios.get(
      `/api/v1/accounts/${accountId.value}/contacts/${contactId}/shipments`
    );
    // The user may have switched contacts while this was in flight — drop a
    // stale response instead of painting it over the current contact's pill.
    if (currentContact.value?.id !== contactId) return;
    const rows = response.data?.rows || [];
    const next = rows.length > 0 ? rows[0] : null;
    shipment.value = next;
    shipmentCache.set(contactId, next);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to fetch shipments:', error);
    // Keep the last known pill on a transient error instead of blanking.
  } finally {
    if (currentContact.value?.id === contactId) {
      isLoadingShipment.value = false;
    }
  }
};

const refreshShipment = async () => {
  if (!shipment.value || isRefreshingShipment.value) return;
  const contactId = shipmentContactId.value;
  if (!contactId) return;

  isRefreshingShipment.value = true;
  try {
    const shippingNumber = shipment.value.shipping_number;
    await axios.post(
      `/api/v1/accounts/${accountId.value}/contacts/${contactId}/refresh_shipment`,
      {
        shipping_number: shippingNumber,
      }
    );
    await fetchShipments();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to refresh shipment:', error);
  } finally {
    isRefreshingShipment.value = false;
  }
};

watch(
  () => currentContact.value?.id,
  newId => {
    if (newId) {
      // Paint this contact's cached pill (or blank if never loaded) before the
      // background refresh, so switching back to a chat never flashes empty.
      shipment.value = shipmentCache.has(newId)
        ? shipmentCache.get(newId)
        : null;
      fetchShipments();
    } else {
      shipment.value = null;
    }
  },
  { immediate: true }
);

watch(
  () => isErpLinked.value,
  linked => {
    if (linked) {
      // Refresh in the background — fetchShipments keeps the cached pill visible
      // meanwhile, so a transient link re-evaluation no longer blanks the pill.
      fetchShipments();
    } else {
      // Genuinely unlinked — hide the pill and drop the stale cache entry.
      shipment.value = null;
      const contactId = currentContact.value?.id;
      if (contactId) shipmentCache.delete(contactId);
    }
  }
);

// Full-tracking modal (opened by clicking the shipment pill).
const showTrackingModal = ref(false);
const openTrackingModal = () => {
  showTrackingModal.value = true;
};
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex flex-col gap-3 items-center justify-between flex-1 w-full min-w-0 xl:flex-row px-3 py-2 h-auto min-h-24 xl:h-[84px]"
  >
    <div
      class="flex items-center justify-start w-full xl:w-auto max-w-full min-w-0 xl:flex-1"
    >
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="ltr:mr-2 rtl:ml-2"
      />
      <!-- Eltafouk: نفس أيقونة التصنيف اللي في القايمة. من غيرها نفس العميل
           يبقى ماسورة بنفسجية في القايمة وحرفين رماديين هنا — والكومبوزابل
           موجود أصلاً عشان المكانين ما يفرقوش. -->
      <Avatar
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :icon-name="isCommentSection ? null : classificationAvatar?.icon"
        :color-class="
          isCommentSection ? '' : classificationAvatar?.colorClass || ''
        "
        :size="32"
        :status="currentContact.availability_status"
        hide-offline-status
        rounded-full
      />
      <div
        class="flex flex-col items-start min-w-0 ml-2 gap-1.5 overflow-hidden rtl:ml-0 rtl:mr-2"
      >
        <!-- Eltafouk: الصندوق اللي الرسالة جاية عليه — سطر لوحده فوق الاسم.
             كان جوّه صف الأزرار، وسط الزراير، فبيتقري كأنه زرار وهو مجرد
             معلومة. بلاغ المالك ٢٣/٨: «رقم الواتساب اللي جاي عليه الرسالة
             خليه فوق اسم العميل ورقمه». -->
        <InboxName v-if="hasMultipleInboxes" :inbox="inbox" class="!mx-0" />

        <div class="flex flex-row items-center max-w-full gap-1.5 p-0 m-0">
          <!-- Eltafouk: تصنيف العميل في **أول** سطر الاسم — طلب المالك ٢٣/٨.
               كان في صف الأزرار تحت، وسط الأفعال، وهو معلومة عن الشخص نفسه:
               «ده طالب ولا مدرس ولا مكتبة» بيغيّر نبرة الرد من أول كلمة، فمكانه
               جنب اسمه مش جنب الزراير. -->
          <span
            v-if="classificationChip && !isCommentSection"
            class="inline-flex items-center px-2 py-px rounded-md text-[11px] font-bold leading-relaxed flex-shrink-0"
            :class="classificationChip.softClass"
          >
            {{ classificationChip.label }}
          </span>
          <span
            class="text-sm font-semibold truncate leading-tight text-n-slate-12"
          >
            {{ currentContact.name }}
          </span>
          <!-- Eltafouk: علامة «عميل عندنا» — زي علامة التوثيق.
               🚨 مربوطة بوجود العميل في الـERP وبس، **مش** بتصنيفه: طالب أو
               مدرس أو مكتبة كلهم عملاء مسجّلين. التصنيف ليه شريحته في صف
               الأزرار، ودي بتجاوب على سؤال تاني خالص — «ده عميل عندنا ولا
               حد بيسأل؟» — والموظفة محتاجة تعرفه بنظرة قبل ما تبص على أي
               حاجة تانية. -->
          <!-- 🚨 علامة مصمتة (أبيض على أزرق) مش أيقونة خطّية — طلب المالك
               ٢٣/٨: «تكون بولد وواضحة أوي زي علامة التوثيق الزرقاء لفيسبوك».
               الأيقونة الخطّية كانت بتضيع وسط النص الرمادي حواليها. الشكل ده
               بيدي كتلة لون تتشاف من بعيد، وهو اللي بيخلّي علامة فيسبوك تشتغل. -->
          <span
            v-if="isErpLinked && !isCommentSection"
            v-tooltip.bottom="$t('CONVERSATION.HEADER.VERIFIED_CUSTOMER')"
            class="inline-flex items-center justify-center size-[18px] rounded-full bg-n-blue-9 text-white shadow-sm flex-shrink-0"
          >
            <span class="i-lucide-check size-3 stroke-[3]" />
          </span>
          <!-- Eltafouk: اسم العميل في الـERP جنب اسمه على القناة.
               مابيستبدلش الاسم — الاتنين ظاهرين. `min-w-0` على الحاوية مع
               `truncate` جوّه عشان اسم طويل يتقص بدل ما يزقّ باقي السطر بره. -->
          <span
            v-if="erpCustomerName && !isCommentSection"
            v-tooltip.bottom="$t('CONVERSATION.HEADER.ERP_CUSTOMER_NAME')"
            class="inline-flex items-center gap-1 min-w-0 ltr:pl-1.5 rtl:pr-1.5 ltr:border-l rtl:border-r border-n-strong leading-none"
          >
            <span
              class="i-lucide-contact-round size-3 text-n-blue-10 flex-shrink-0"
            />
            <span class="text-[11px] font-semibold text-n-slate-11 truncate">
              {{ erpCustomerName }}
            </span>
          </span>
          <span
            v-if="formattedPhoneNumber"
            class="inline-flex items-center gap-1 flex-shrink-0 ltr:pl-1.5 rtl:pr-1.5 ltr:border-l rtl:border-r border-n-strong leading-none"
            dir="ltr"
          >
            <span class="i-lucide-phone size-3 text-n-teal-10 flex-shrink-0" />
            <span
              class="text-[11px] font-semibold text-n-slate-11 tabular-nums"
            >
              {{ formattedPhoneNumber }}
            </span>
          </span>
          <span
            v-else-if="whatsAppHandle"
            v-tooltip="$t('CONVERSATION.HEADER.WHATSAPP_USERNAME')"
            class="inline-flex items-center gap-1 flex-shrink-0 ltr:pl-1.5 rtl:pr-1.5 ltr:border-l rtl:border-r border-n-strong leading-none"
            dir="ltr"
          >
            <span
              class="i-lucide-at-sign size-3 text-n-teal-10 flex-shrink-0"
            />
            <span class="text-[11px] font-semibold text-n-slate-11 truncate">
              {{ whatsAppHandle }}
            </span>
          </span>
          <fluent-icon
            v-if="!isHMACVerified"
            v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
            size="14"
            class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
            icon="warning"
          />
        </div>

        <div
          class="flex items-center gap-2.5 overflow-x-auto text-xs conversation--header--actions text-ellipsis whitespace-nowrap"
        >
          <span v-if="isSnoozed" class="font-medium text-n-amber-10">
            {{ snoozedDisplayText }}
          </span>

          <!-- Eltafouk: مواد المدرس. كانت رمادية محايدة (`bg-n-alpha-2` +
               `text-n-slate-11`) — والمشكلة مكانتش التباين، ده كان 5.79 يعني
               مقروء؛ المشكلة إن الرمادي **محايد** فالعين بتعدّي عليه وسط صف
               مليان أزرار (بلاغ المالك ٢٥/٨: «عاوز الليبل بتاعها يكون أوضح»).
               بقت بخلفية المدرس البنفسجية فبقت تتقري كإنها جزء من هويته مش
               عنصر تايه، ونص درجة ١٢ بيدي 11.99/11.67 بدل 5.79.

               🚨 الخلفية جاية من `detailClass` مش مكتوبة هنا: لون المدرس
               بيتقرر في CLASSIFICATION_STYLES مرة واحدة، ولو اتكتب هنا نسخة
               تانية أول تعديل عليه بيخلّي الهيدر يعرض بنفسجيين مختلفين.
               ⚠️ مش نسخة طبق الأصل من شريحة «مدرس» اللي في السطر اللي فوق:
               دي `rounded-full` ونص درجة ١٢، وهي `rounded-md` بولد ودرجة ١١.
               الخلفية بس هي المشتركة. -->
          <span
            v-if="teacherSubject && !isCommentSection && classificationChip"
            v-tooltip.top="
              `${t('CONVERSATION.HEADER.TEACHER_SUBJECT')}: ${teacherSubject}`
            "
            class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold leading-relaxed flex-shrink-0 max-w-[27rem]"
            :class="classificationChip.detailClass"
          >
            <span class="i-lucide-book-open size-3.5 flex-shrink-0" />
            <span class="truncate">{{ teacherSubject }}</span>
          </span>

          <!-- Eltafouk: مجموعة الـERP — ملف العميل + أوردراته + فواتيره + كشف حسابه.

               🚨 بلاغ المالك ٢٣/٨: «الأزرار مش واضحة ومش فاهمين بتوصل لفين».
               السبب كان إن التلاتة دول **أيقونات صامتة** والاسم مخبّي في تلميح
               بيظهر بعد ثانية وقوف. والأسماء القصيرة (`ERP_LINKS.*.SHORT`)
               كانت **موجودة في الترجمة من الأول ومترسمتش ولا مرة**.

               دلوقتي الاسم مكتوب. وبيختفي تحت `lg` بس — عشان السطر ده بيزيد عن
               الشاشة الضيّقة، وساعتها الأيقونة + التلميح بيرجعوا زي ما كانوا
               بدل ما الزراير تتقص بره الشاشة.

               والمقاسات كبرت (11px → 12px، وارتفاع 24 → 26) لأن العربي بيتقفّل
               على 11px: النقط تحت «ي» و«ب» بتتاكل في صندوق ضيّق. -->
          <!-- Eltafouk: زراير الـERP.
               🚨 بلاغ المالك ٢٣/٨: «محتاج شكلها يكون أوضح وواضح إنها أزرار
               قابلة للضغط». الجولة اللي فاتت شلت الصندوق عشان الوزن يخف، بس
               النتيجة إنها بقت نص سايب مالوش أي إشارة إنه بيتداس. الوسط
               الصحيح: **كل زرار بحدود وخلفية خاصة بيه** — كتلة تتشاف من بعيد
               وتقول «أنا زرار» من غير ما نرجّع الصندوق الكبير اللي كان تقيل.
               والـ`active:scale` بيدي رد فعل عند الضغط. -->
          <div
            v-if="erpCustomerUrl && !isCommentSection"
            class="inline-flex items-center gap-1.5 flex-shrink-0"
          >
            <a
              v-tooltip.top="t('CONVERSATION.HEADER.OPEN_CUSTOMER_IN_ERP')"
              :href="erpCustomerUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-1 h-[26px] px-2.5 rounded-md border text-[11px] font-bold leading-relaxed cursor-pointer transition-all active:scale-[0.97] shadow-sm"
              :class="
                isErpLinked
                  ? 'border-n-teal-6 bg-n-teal-3 text-n-teal-11 hover:brightness-95'
                  : 'border-n-strong bg-n-solid-1 text-n-slate-11 hover:bg-n-alpha-2'
              "
            >
              <span
                v-if="isErpLinked"
                class="i-lucide-check size-3 flex-shrink-0"
              />
              {{ erpLabel }}
            </a>
            <a
              v-for="link in erpLinks"
              :key="link.key"
              v-tooltip.top="link.label"
              :href="link.url"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center justify-center gap-1.5 h-[26px] px-2.5 rounded-md border border-n-strong bg-n-solid-1 text-[11px] font-semibold leading-relaxed text-n-slate-11 shadow-sm hover:bg-n-alpha-2 hover:text-n-slate-12 hover:border-n-slate-7 cursor-pointer transition-all active:scale-[0.97]"
            >
              <span class="size-3.5 flex-shrink-0" :class="link.icon" />
              <span class="hidden lg:inline">{{ link.short }}</span>
              <!-- العدّاد جوّه الزرار نفسه، مش شريحة منفصلة: الرقم بيوصف
                   الزرار ده بالتحديد، ولو عاش جنبه العين هتلمّه على الزرار
                   اللي بعده. `null` = لسه ماردّش، فمابنعرضش صفر كذب. -->
              <!-- العدّاد بلون بيقول حاجة، مش رمادي محايد:
                   · أوردرات مفتوحة > صفر = شغل مستنّي ⇒ **كهرماني**، بيشد العين.
                   · صفر = مافيش شغل ⇒ رمادي هادي، موجود عشان الموظفة تعرف إن
                     الرقم اتقاس فعلاً مش إن الصفحة لسه بتحمّل.
                   · الفواتير أزرق = معلومة أرشيفية، مش مطلوب منها حاجة. -->
              <span
                v-if="countFor(link.key) !== null"
                class="inline-flex items-center justify-center min-w-[17px] h-[17px] px-1 rounded-full text-[10px] font-bold tabular-nums shadow-sm"
                :class="countTone(link.key)"
              >
                {{ countFor(link.key) }}
              </span>
            </a>
          </div>

          <!-- Eltafouk: Shipment Tracking Pill -->
          <div
            v-if="shipment && !isCommentSection"
            class="inline-flex items-center gap-0.5 p-0.5 rounded-lg border border-n-weak bg-n-alpha-1 flex-shrink-0"
          >
            <button
              v-tooltip.top="{
                content: t('CONVERSATION.HEADER.VIEW_FULL_TRACKING'),
                classes: 'text-xs max-w-xs',
              }"
              class="inline-flex items-center gap-1 h-[22px] px-2 rounded text-[11px] font-bold leading-relaxed bg-n-blue-3 text-n-blue-11 hover:brightness-95 cursor-pointer transition"
              @click="openTrackingModal"
            >
              <span class="i-lucide-truck size-3 flex-shrink-0" />
              <span>{{
                shipment.status_display || shipment.status_ar || shipment.status
              }}</span>
              <span class="text-n-blue-9" dir="ltr">
                {{ t('CONVERSATION.HEADER.HASH')
                }}{{ shipment.shipping_number }}
              </span>
            </button>
            <button
              v-tooltip.top="t('CONVERSATION.HEADER.REFRESH_SHIPMENT')"
              class="inline-flex items-center justify-center size-7 rounded-md border border-n-weak bg-n-solid-1 text-n-slate-11 hover:bg-n-teal-3 hover:text-n-teal-11 hover:border-n-teal-6 cursor-pointer transition-colors"
              :disabled="isRefreshingShipment"
              @click="refreshShipment"
            >
              <span
                class="size-4 i-lucide-rotate-cw"
                :class="{ 'animate-spin': isRefreshingShipment }"
              />
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      class="flex flex-row items-center justify-start xl:justify-end flex-shrink-0 gap-2 w-full xl:w-auto header-actions-wrap"
    >
      <!-- Eltafouk: «أوردر جديد» جنب «حل المحادثة» — طلب المالك ٢٣/٨.
           كان أول عنصر في صف الشرايح، وسط المعلومات، فبيتقري كأنه شريحة تانية.
           مكانه الطبيعي جنب الفعل التاني الوحيد في الشاشة.

           🚨 والتعليق القديم («لازم يبقى أول عنصر وإلا بيتقص») مابقاش ينطبق:
           كان بيتكلم عن صف الشرايح اللي بيزيد عن الشاشة وآخره بيتقص. الصف ده
           حاوية تانية خالص (`header-actions-wrap`) عرضها بيتحسب من محتواها
           وفيها زرارين بس، فمافيش قص. -->
      <a
        v-if="createOrderUrl"
        v-tooltip.bottom="t('CONVERSATION.HEADER.CREATE_ORDER.TOOLTIP')"
        :href="createOrderUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-bold leading-relaxed bg-n-slate-12 text-n-slate-1 shadow cursor-pointer transition-all hover:brightness-125 active:scale-[0.98] flex-shrink-0"
      >
        <span class="i-lucide-file-plus-2 size-3.5 flex-shrink-0" />
        {{ t('CONVERSATION.HEADER.CREATE_ORDER.LABEL') }}
      </a>
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <MoreActions
        :conversation-id="currentChat.id"
        :is-comment-section="isCommentSection"
      />
    </div>
    <ShipmentTrackingModal
      v-if="shipment && currentContact.id"
      v-model:show="showTrackingModal"
      :account-id="accountId"
      :contact-id="currentContact.id"
      :conversation-id="currentChat.id"
      :shipment="shipment"
      :can-reply="currentChat.can_reply"
    />
  </div>
</template>
